// #region imports

// --- Angular
import { Component, OnInit, OnDestroy } from '@angular/core';

// --- RxJs
import { ArgumentOutOfRangeError, Observable, Subscription } from 'rxjs';

// --- 3rd Party
import _, { flatten } from 'lodash';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  GroupModel,
  SurveySummaryModel,
  Constants,
  RespondentModel,
  InvitationTypes,
  RespondentEmailValidationModel,
  LanguageModel,
  SortDirections,
  ParticipantValidationDestinations,
} from '../../../models';
import {
  ParticipantGroupsBaseService,
  SurveyBaseService,
  RespondentReminderBaseService,
  RespondentsBaseService,
  OidcService,
  LanguagesBaseService,
} from '../../../core/services';
import { User } from 'oidc-client';

// #endregion

@Component({
  selector: 'hs-manage-respondents-mobile-shell',
  templateUrl: './manage-respondents-mobile-shell.component.html',
  styleUrls: ['./manage-respondents-mobile-shell.component.scss']
})
export class ManageRespondentsMobileShellComponent implements OnInit {
  selectedSurvey$: Observable<SurveySummaryModel | null> | undefined;
  participantGroups$: Observable<GroupModel[]> | undefined;
  validatedRespondent$: Observable<RespondentEmailValidationModel | null> | undefined;
  validatedRespondent: RespondentEmailValidationModel | undefined;
  identity$: Observable<User | null> | undefined;
  languages$: Observable<LanguageModel[]> | undefined;

  groups: GroupModel[] = new Array<GroupModel>();

  private _respondentEmailSubscription: Subscription = Subscription.EMPTY;

  constructor(
    private surveyService: SurveyBaseService,
    private groupsService: ParticipantGroupsBaseService,
    private respondentReminderService: RespondentReminderBaseService,
    private respondentService: RespondentsBaseService,
    private oidcService: OidcService,
    private languageService: LanguagesBaseService
  ) { }

  ngOnInit() {
    localStorage.setItem(
      Constants.PARTICIPANT_REDIRECT_DESTINATION_KEY,
      ParticipantValidationDestinations.none);

    this.selectedSurvey$ = this.surveyService.selectedSelfSurvey$;
    this.participantGroups$ = this.groupsService.participantGroups$;
    this.validatedRespondent$ = this.respondentService.respondentBounceCheck$;
    this._respondentEmailSubscription = this.respondentService.respondentBounceCheck$.subscribe(model => {
      if (!model) {
        return;
      }
      this.validatedRespondent = model;
    });
    this.identity$ = this.oidcService.identity$;
    this.languages$ = this.languageService.languages$;
    this.surveyService.loadSummaries(Constants.MAXIMUM_NUMBER_OF_SELF_SURVEYS, 1, '', '', SortDirections.descending);
  }

  ngOnDestroy(): void {
    if (this._respondentEmailSubscription) {
      this._respondentEmailSubscription.unsubscribe();
      this._respondentEmailSubscription = Subscription.EMPTY;
    }
    localStorage.removeItem(Constants.PARTICIPANT_REDIRECT_DESTINATION_KEY);
  }

  onGroupsUpdated(groups: GroupModel[]): void {
    this.groups = groups;
  }

  onAddGroup(surveyId: string) {
    this.groupsService.addEmpty(this.groupsService.createGroup(surveyId));
  }

  onGroupDeleted(group: GroupModel): void {
    this.groupsService.delete(group);
  }

  onSaveClick(groups: GroupModel[]): void {
    const groupsToUpdate = groups.filter((g) => g.isNew || g.isDirty);
    this.groupsService.update(groupsToUpdate);
  }

  canSave(): boolean {
    if (!this.groups) {
      return false;
    }

    const isNew = this.groups.filter((g) => g.isNew && g.isValid).length > 0;
    const isDirty = this.groups.filter((g) => g.isDirty).length > 0;

    return (
      (isNew || isDirty) &&
      this.groups[0].respondents &&
      this.groups[0].respondents.length ===
      Constants.NUMBER_OF_RESPONDENTS_PER_GROUP
    );
  }

  onRemindRespondentClick(respondent: RespondentModel) {
    this.respondentReminderService.reminderRequest({
      recipientId: respondent.id ?? '',
      senderId: respondent.participantId ?? '',
      invitationType: InvitationTypes.respondentReminder,
    });
  }

  onEmailAddressChanged(model: RespondentEmailValidationModel): void {
    this.respondentService.checkForRespondentEmailBounce(model);
  }

  onResetValidatedRespondent() {
    this.respondentService.resetRespondentBounceCheck();
  }

  onDismissClick() {
    localStorage.removeItem(Constants.PARTICIPANT_REDIRECT_DESTINATION_KEY);
    window.location.href = environment.mobileCallbackUrl;
    return;
  }
}
